import React, { useEffect, useState } from "react";
import { Button, Card, Col, Flex, Form, Input, Row, Select, Spin } from "antd";
import { useWatch } from "antd/es/form/Form";
import { IMasterDataSupplier } from "../../../interfaces/master-data";
import DynamicPaymentTable from "../../../components/dynamic-payment-table";
import { MasterDataServices } from "../../../services/master-data";
import { SystemDefaultServices } from "../../../services/system-default";
import { useMessageApi } from "../../../context/MessageContext";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { PERMISSIONS } from "../../../constants/permission";

const TermDefault = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();

  const [loading, setLoading] = useState(false);
  const [loadingScreen, setLoadingScreen] = useState(true);
  const hasSystemDefaultEditor = useHasPermission([PERMISSIONS.SYSTEM_DEFAULT_EDITOR]);

  const noOfInstallments: number = useWatch("noOfInstallments", form) ?? 0;
  const stagesValue = useWatch("stages", form) ?? {};
  const depositValue = useWatch("deposit", form) ?? {};
  const finalValue = useWatch("final", form) ?? {};

  const [masterData, setMasterData] = useState<IMasterDataSupplier>({
    termTypes: [],
    installments: [],
    eventTypes: [],
    timings: [],
    absoluteDrivers: [],
    calculationBasis: [],
    accountDebits: [],
    accountCredits: [],
    autosMatch: [],
    users: [],
    defaultTermsRule: {
      deposit: undefined,
      stages: [],
      final: {
        calculationBasis: "Percentage",
        amount: 100,
        eventType: undefined,
        timing: undefined,
        absoluteDriver: undefined,
        duration: undefined,
      },
    },
  });

  const { installments, eventTypes, timings, absoluteDrivers, calculationBasis, defaultTermsRule } = masterData;

  const fetchMasterData = async () => {
    setLoadingScreen(true);
    try {
      const types = ["TERM_TYPE", "NUM_OF_INSTALLMENT", "EVENT_TYPE", "TIMING", "ABSOLUTE_DRIVER", "CALCULATION_BASIS"];

      const masterPromises = types.map((type) => MasterDataServices.getAllByType(type).then((res) => res?.data || []));

      const defaultTermsRule = await SystemDefaultServices.getDefaultTermsRule({ type: "PAYMENT_TERM" }).then(
        (res) => res?.data || {}
      );

      const [termTypes, installments, eventTypes, timings, absoluteDrivers, calculationBasis] = await Promise.all(
        masterPromises
      );

      setMasterData({
        termTypes,
        installments,
        eventTypes,
        timings,
        absoluteDrivers,
        calculationBasis,
        defaultTermsRule,
      });
    } catch (error) {
      console.error("Error fetching master data:", error);
      messageApi.error("Failed to load master data.");
    } finally {
      setLoadingScreen(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  const addIndexPaymenterm = (term: any) => {
    const result = { ...term };
    let idx = 0;
    for (const [key, val] of Object.entries(term)) {
      if (Array.isArray(val)) {
        result[key] = val.map((item, i) => ({
          ...item,
          index: i,
        }));
      } else if (val !== null && typeof val === "object") {
        result[key] = {
          ...val,
          index: idx++,
        };
      } else {
        result[key] = val;
      }
    }
    return result;
  };

  useEffect(() => {
    if (defaultTermsRule && Object.keys(defaultTermsRule).length > 0) {
      form.setFieldsValue({ ...defaultTermsRule });
    }
  }, [defaultTermsRule, form]);

  const handleChangeNoInstallment = (value: string) => {
    const termName = installments?.find((item) => item.code === (value.toString() ?? ""));
    form.setFieldValue("termName", termName?.label ?? "");
    form.setFieldValue("noOfInstallments", value);

    const defaultInstallments = defaultTermsRule.noOfInstallments;
    const currentTerm = form.getFieldsValue();

    if (defaultInstallments && value !== defaultInstallments) {
      if (currentTerm?.deposit || currentTerm?.stages || currentTerm?.final) {
        form.setFieldsValue({
          deposit: undefined,
          stages: [],
          final: {
            calculationBasis: "Percentage",
            amount: 100,
            eventType: undefined,
            timing: undefined,
            absoluteDriver: undefined,
            duration: undefined,
          },
        });
      }
    } else {
      form.setFieldsValue({ ...defaultTermsRule });
    }
  };

  const handleSubmitAll = async () => {
    try {
      const termsValues = await form.validateFields();

      const payload = {
        ...addIndexPaymenterm(termsValues),
      };
      setLoading(true);
      const response: any = await SystemDefaultServices.updateSystemDefaultTerm(payload);

      if (response.success) {
        messageApi.success(response.message);
        const newDefault = await SystemDefaultServices.getDefaultTermsRule({ type: "PAYMENT_TERM" }).then(
          (res) => res?.data || {}
        );
        setMasterData({ ...masterData, defaultTermsRule: newDefault });
      }
    } catch (err: any) {
      messageApi.error(err?.message || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingScreen) {
    return (
      <Flex gap="middle" vertical style={{ padding: "2rem" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Form form={form}>
      <Flex vertical>
        {hasSystemDefaultEditor ? (
          <Flex gap={16} style={{ marginBottom: 15 }} justify="end">
            <Button type="primary" onClick={handleSubmitAll} loading={loading}>
              Save
            </Button>
          </Flex>
        ) : null}
        <Card className="payment-item">
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="noOfInstallments"
                label="Number of Installments"
                rules={[{ required: true, message: "Please select number of installments" }]}
              >
                <Select
                  placeholder="Select"
                  options={installments?.map((_, index) => ({
                    value: index + 1,
                    label: (index + 1).toString(),
                  }))}
                  onChange={handleChangeNoInstallment}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="termName" label="Term Structure">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          {noOfInstallments >= 2 && (
            <>
              <h4>Deposit</h4>
              <DynamicPaymentTable
                name="deposit"
                eventTypes={eventTypes ?? []}
                timings={timings ?? []}
                absoluteDrivers={absoluteDrivers ?? []}
                calculationBasis={calculationBasis ?? []}
                values={depositValue}
              />
            </>
          )}

          {noOfInstallments >= 3 && (
            <>
              <h4>Stage Payments</h4>
              <DynamicPaymentTable
                name="stages"
                useRowIndex={true}
                rowCount={noOfInstallments - 2}
                eventTypes={eventTypes ?? []}
                timings={timings ?? []}
                absoluteDrivers={absoluteDrivers ?? []}
                calculationBasis={calculationBasis ?? []}
                values={stagesValue}
              />
            </>
          )}

          {noOfInstallments >= 1 && (
            <>
              <h4>Final</h4>
              <DynamicPaymentTable
                name="final"
                eventTypes={eventTypes ?? []}
                timings={timings ?? []}
                absoluteDrivers={absoluteDrivers ?? []}
                calculationBasis={calculationBasis ?? []}
                values={finalValue}
              />
            </>
          )}
        </Card>
      </Flex>
    </Form>
  );
};

export default TermDefault;
